import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { client } from '../api';
import type { TaskSummary, TasksResponse } from '../api';
import { PartnerTaskStatusFilter } from '../api';
import PageContainer from '../components/PageContainer';
import TasksTable from '../components/tasks/TasksTable';
import { Outlet, useNavigate } from 'react-router-dom';
import { tasksQueryKey } from '../queries';
import { formatStandardDate, formatStandardDateLocaleString } from '../utils';
import Button from 'design-system/src/components/Button/Button';

const TasksTablePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status') || PartnerTaskStatusFilter.OPEN; // Default to 'open' (Requires response)

  const { data, isLoading, error } = useQuery<TasksResponse>({
    queryKey: tasksQueryKey(status),
    queryFn: () =>
      client.get('/tasks', { params: { status } }).then((res) => {
        res.data.tasks = res.data.tasks.map((item: TaskSummary) => ({
          ...item,
          last_comment_created_at: formatStandardDateLocaleString(item.last_comment_created_at),
          patient_dob: formatStandardDate(item.patient_dob),
          drug: {
            ...item.drug,
            // We format dates here instead of in TasksTable so that the global search function applies to the formatted data
            prescription_recieved_at: item.drug
              ? formatStandardDateLocaleString(item.drug.prescription_recieved_at)
              : null,
          },
        }));

        return res.data;
      }),
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const newTaskButton = (
    <Button size="sm" onClick={() => navigate('new?source=portal_task_page')}>
      Create task
    </Button>
  );

  return (
    <>
      <PageContainer
        title="Tasks"
        subtitle="These require input from your team to move prescriptions forward."
        headerRight={newTaskButton}
      >
        <TasksTable data={data?.tasks || []} isLoading={isLoading} error={error} />
      </PageContainer>
      <Outlet
        context={{
          reload: () => queryClient.resetQueries({ queryKey: tasksQueryKey(status) }),
        }}
      />
    </>
  );
};

export default TasksTablePage;
